import { ColorTokens } from '../color-tokens';
import { ThemeConfig, ThemeVariables } from '../theme-types';

const colorTokens: ColorTokens = {
  // Brand colors
  'colors-brand-green-1': '#e6eef5',
  'colors-brand-green-2': '#c2d6e8',
  'colors-brand-green-3': '#91b5d6',
  'colors-brand-green-4': '#5e93c3',
  'colors-brand-green-5': '#2e72b0',
  'colors-brand-green-6': '#00539f',
  'colors-brand-green-7': '#004787',
  'colors-brand-green-8': '#003b71',
  'colors-brand-green-9': '#002f5b',
  'colors-brand-green-10': '#002548',

  /* Destructive colors - SimLocal copy */
  'colors-destructive-red-1': '#feeceb',
  'colors-destructive-red-2': '#fbd2cf',
  'colors-destructive-red-3': '#f9afa9',
  'colors-destructive-red-4': '#f68982',
  'colors-destructive-red-5': '#f3665c',
  'colors-destructive-red-6': '#f04438',
  'colors-destructive-red-7': '#cc3a30',
  'colors-destructive-red-8': '#aa3028',
  'colors-destructive-red-9': '#892720',
  'colors-destructive-red-10': '#6c1f19',

  /* Success colors - SimLocal copy */
  'colors-success-green-1': '#e7f8f0',
  'colors-success-green-2': '#c6eedb',
  'colors-success-green-3': '#99e0bf',
  'colors-success-green-4': '#6ad2a1',
  'colors-success-green-5': '#3dc485',
  'colors-success-green-6': '#12b76a',
  'colors-success-green-7': '#0f9c5a',
  'colors-success-green-8': '#0d824b',
  'colors-success-green-9': '#0a683c',
  'colors-success-green-10': '#085230',

  /* Neutral colors */
  'colors-neutral-1': '#ffffff',
  'colors-neutral-2': '#fcfcfc',
  'colors-neutral-3': '#f5f5f5',
  'colors-neutral-4': '#f0f0f0',
  'colors-neutral-5': '#cdcdcd',
  'colors-neutral-6': '#a6a5a6',
  'colors-neutral-7': '#7c7b7d',
  'colors-neutral-8': '#545355',
  'colors-neutral-9': '#2f2d30',
  'colors-neutral-10': '#282629',
  'colors-neutral-11': '#212022',
  'colors-neutral-12': '#151416',
  'colors-neutral-13': '#000000',

  /* Warning colors - SimLocal copy */
  'colors-warning-orange-1': '#fef4e6',
  'colors-warning-orange-2': '#fde4c4',
  'colors-warning-orange-3': '#fccf95',
  'colors-warning-orange-4': '#fab964',
  'colors-warning-orange-5': '#f8a435',
  'colors-warning-orange-6': '#f79009',
  'colors-warning-orange-7': '#d27a08',
  'colors-warning-orange-8': '#af6606',
  'colors-warning-orange-9': '#8d5205',
  'colors-warning-orange-10': '#6f4104',

  /* Informative colors - SimLocal copy */
  'colors-informative-blue-1': '#e7f6fd',
  'colors-informative-blue-2': '#c4e9fa',
  'colors-informative-blue-3': '#96d8f7',
  'colors-informative-blue-4': '#65c6f3',
  'colors-informative-blue-5': '#37b5ef',
  'colors-informative-blue-6': '#0ba5ec',
  'colors-informative-blue-7': '#098cc9',
  'colors-informative-blue-8': '#0875a8',
  'colors-informative-blue-9': '#065e87',
  'colors-informative-blue-10': '#054a6a',

  /* Support colors - Violet / Tesco Red */
  'colors-support-violet-1': '#fde8ea',
  'colors-support-violet-2': '#f9c9cd',
  'colors-support-violet-3': '#f59da5',
  'colors-support-violet-4': '#f1707b',
  'colors-support-violet-5': '#ec4553',
  'colors-support-violet-6': '#e81c2d',
  'colors-support-violet-7': '#c51826',
  'colors-support-violet-8': '#a51420',
  'colors-support-violet-9': '#84101a',
  'colors-support-violet-10': '#680d14',

  /* Support colors - Blue / Tesco Yellow */
  'colors-support-blue-1': '#fffbe6',
  'colors-support-blue-2': '#fef6c2',
  'colors-support-blue-3': '#feee91',
  'colors-support-blue-4': '#fde65e',
  'colors-support-blue-5': '#fddf2e',
  'colors-support-blue-6': '#fcd700',
  'colors-support-blue-7': '#d6b800',
  'colors-support-blue-8': '#b39900',
  'colors-support-blue-9': '#907b00',
  'colors-support-blue-10': '#716100',

  /* Support colors - Yellow / Tesco Yellow */
  'colors-support-yellow-1': '#fffbe6',
  'colors-support-yellow-2': '#fef6c2',
  'colors-support-yellow-3': '#feee91',
  'colors-support-yellow-4': '#fde65e',
  'colors-support-yellow-5': '#fddf2e',
  'colors-support-yellow-6': '#fcd700',
  'colors-support-yellow-7': '#d6b800',
  'colors-support-yellow-8': '#b39900',
  'colors-support-yellow-9': '#907b00',
  'colors-support-yellow-10': '#716100',
};

export const variables: ThemeVariables = {
  'background-gradient':
    'linear-gradient(135.05deg, #26a69a 1.74%, #004d40 100%)',
  'color-error': '#f14643',
  'color-primary-1': '#E6EEF5',
  'color-primary-dark': '#003B71',
  'color-primary': '#00539F',
  'color-secondary-1': '#FCE4EC',
  'color-secondary-dark': '#A51420',
  'color-secondary': '#FDE8EA',
  'color-tertiary-1': '#FFFBE6',
  'color-tertiary': '#FCD700',
  'color-tertiary-dark': '#B39900',
  'color-quaternary': '#fbea00',
  'font-color-highlight': 'var(--color-primary)',
  'font-color-primary': '#3a474e',
  'font-color-secondary': '#6e7881',
  'font-family-body': 'TESCOModern',
  'font-family-titles': 'TESCOModern',
  'plan-card-font-color': '#424242',
  'footer-background': 'var(--color-primary)',
  'nav-bar-items-color': 'var(--colors-brand-green-6)',
  ...colorTokens,
};

export const config: ThemeConfig = {
  font: {
    type: 'custom',
    fontHref: '/fonts/tesco/index.css',
  },
};
